import { useMemo, useState } from 'react';
import { Button, Typography, Stack, TextField } from '@mui/material';
import DataTable from '../../components/DataTable';
import FilterPanel from '../../components/FilterPanel';
import PageHeader from '../../components/PageHeader';
const emptyCategory = {
  systemName: '',
  displayNames: {
    he: '',
    en: '',
    es: '',
  },
  placesCount: 0,
};
const normalizeCategory = (category = {}) => ({
  ...emptyCategory,
  ...category,
  displayNames: {
    he: category.displayNames?.he || '',
    en: category.displayNames?.en || category.displayName || '',
    es: category.displayNames?.es || '',
  },
});
const PlaceCategoriesPage = ({ categories, onCreate, onSave, onDelete, loading }) => {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const rows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return categories.filter(
      (category) =>
        !query ||
        [
          category.systemName,
          category.displayName,
          category.displayNames?.he,
          category.displayNames?.en,
          category.displayNames?.es,
        ].some((value) =>
          String(value || '')
            .toLocaleLowerCase()
            .includes(query),
        ),
    );
  }, [categories, search]);
  const openNew = () =>
    setEditing({
      ...emptyCategory,
      displayNames: {
        ...emptyCategory.displayNames,
      },
      isNew: true,
    });
  const openEdit = (category) =>
    setEditing({
      ...normalizeCategory(category),
      isNew: false,
    });
  const update = (field, value) =>
    setEditing((current) => ({
      ...current,
      [field]: value,
    }));
  const updateName = (language, value) =>
    setEditing((current) => ({
      ...current,
      displayNames: {
        ...current.displayNames,
        [language]: value,
      },
    }));
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        systemName: editing.systemName,
        displayNames: editing.displayNames,
      };
      if (editing.isNew) await onCreate(payload);
      else await onSave(editing._id, payload);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Stack
      sx={{
        display: 'grid',
        gap: '20px',
        height: '100%',
        minHeight: 0,
        gridTemplateRows: 'auto minmax(0, 1fr)',
      }}
    >
      <PageHeader
        title="קטגוריות"
        meta={`(${categories.length})`}
        actions={
          <Button variant="contained" onClick={openNew}>
            הוספת קטגוריה
          </Button>
        }
      >
        <FilterPanel>
          <TextField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="חיפוש קטגוריה"
          />
        </FilterPanel>
      </PageHeader>
      <Stack
        sx={{
          minHeight: 0,
          height: '100%',
        }}
      >
        <DataTable
          columns={[
            {
              key: 'systemName',
              label: 'שם מערכת',
              minWidth: 170,
              render: (row) => row.systemName,
            },
            {
              key: 'hebrewName',
              label: 'עברית',
              minWidth: 150,
              render: (row) => normalizeCategory(row).displayNames.he || '—',
            },
            {
              key: 'englishName',
              label: 'אנגלית (ברירת מחדל)',
              minWidth: 190,
              render: (row) => normalizeCategory(row).displayNames.en || '—',
            },
            {
              key: 'placesCount',
              label: 'מקומות משויכים',
              width: 140,
              render: (row) => {
                const count = Number(row.placesCount) || 0;
                return (
                  <Typography
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 34,
                      minHeight: 24,
                      px: 1.25,
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 800,
                      bgcolor: count > 0 ? 'primary.main' : 'warning.light',
                      color: count > 0 ? 'primary.contrastText' : 'warning.dark',
                    }}
                  >
                    {count}
                  </Typography>
                );
              },
            },
            {
              key: 'actions',
              label: 'פעולות',
              width: 180,
              render: (row) => {
                const category = normalizeCategory(row);
                const inUse = Number(category.placesCount) > 0;
                return (
                  <Stack
                    direction="row"
                    sx={{
                      gap: 1,
                    }}
                  >
                    <Button onClick={() => openEdit(category)}>עריכה</Button>
                    <Button
                      color="error"
                      variant="outlined"
                      title={inUse ? 'לא ניתן למחוק קטגוריה שמשויכת למקום' : ''}
                      disabled={loading || inUse}
                      onClick={() => onDelete(category)}
                    >
                      מחיקה
                    </Button>
                  </Stack>
                );
              },
            },
          ]}
          items={rows}
          rowKey={(category, index) => category._id || category.systemName || index}
          page={1}
          limit={Math.max(rows.length, 1)}
          total={rows.length}
          onPageChange={() => {}}
          loading={loading}
          wide
        />
      </Stack>

      {editing && (
        <Stack
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !saving) setEditing(null);
          }}
          sx={{
            position: 'fixed',
            inset: '0',
            zIndex: '1000',
            display: 'grid',
            placeItems: 'center',
            padding: '20px',
            background: 'rgba(15, 23, 42, .55)',
          }}
        >
          <Stack
            onSubmit={submit}
            sx={{
              width: 'min(620px, 100%)',
              maxHeight: 'calc(100vh - 40px)',
              overflow: 'auto',
              display: 'grid',
              gap: '16px',
              padding: '22px',
              borderRadius: '18px',
              background: '#fff',
              boxShadow: '0 24px 70px rgba(15, 23, 42, .28)',
              '& header': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              },
              '& footer': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '12px',
                borderTop: '1px solid var(--line)',
                paddingTop: '16px',
              },
              '& header p': {
                margin: '0',
                marginTop: '4px',
                color: 'var(--muted)',
              },
              '& label': {
                display: 'grid',
                gap: '7px',
              },
            }}
          >
            <Stack>
              <Stack>
                <Typography>{editing.isNew ? 'הוספת קטגוריה' : 'עריכת קטגוריה'}</Typography>
              </Stack>
              <Button
                type="button"
                onClick={() => setEditing(null)}
                sx={{
                  background: '#edf2f7',
                  color: 'var(--text)',
                  '&:hover': {
                    background: '#ddebff',
                    color: '#1269e8',
                  },
                }}
              >
                סגירה
              </Button>
            </Stack>
            <Stack>
              שם מערכת{' '}
              <TextField
                required
                dir="ltr"
                pattern="[a-z0-9_]+"
                disabled={!editing.isNew && Number(editing.placesCount) > 0}
                value={editing.systemName}
                onChange={(event) =>
                  update('systemName', event.target.value.toLowerCase().replace(/\s+/g, '_'))
                }
                fullWidth
              />
            </Stack>
            {!editing.isNew && Number(editing.placesCount) > 0 && (
              <Typography>שם המערכת נעול כי הקטגוריה משויכת למקומות.</Typography>
            )}
            <Stack
              sx={(theme) => ({
                display: 'grid',
                gap: '7px',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                [theme.breakpoints.down(701)]: {
                  gridTemplateColumns: '1fr',
                },
              })}
            >
              <Stack>
                עברית
                <TextField
                  dir="rtl"
                  value={editing.displayNames.he}
                  onChange={(event) => updateName('he', event.target.value)}
                  placeholder="קניון"
                  fullWidth
                />
              </Stack>
              <Stack>
                אנגלית — חובה
                <TextField
                  required
                  dir="ltr"
                  value={editing.displayNames.en}
                  onChange={(event) => updateName('en', event.target.value)}
                  placeholder="Shopping mall"
                  fullWidth
                />
              </Stack>
              <Stack>
                ספרדית
                <TextField
                  dir="ltr"
                  value={editing.displayNames.es}
                  onChange={(event) => updateName('es', event.target.value)}
                  placeholder="Centro comercial"
                  fullWidth
                />
              </Stack>
            </Stack>
            <Stack>
              <Button
                type="button"
                onClick={() => setEditing(null)}
                disabled={saving}
                sx={{
                  background: '#edf2f7',
                  color: 'var(--text)',
                  '&:hover': {
                    background: '#ddebff',
                    color: '#1269e8',
                  },
                }}
              >
                ביטול
              </Button>
              <Button type="submit" disabled={saving || loading}>
                {saving ? 'שומר…' : 'שמירה'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
export default PlaceCategoriesPage;
